import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Briefcase, DollarSign, Clock, Building2, ArrowLeft, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import api, { getAssetUrl } from '@/utils/api';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import CompanyAvatar from '@/components/CompanyAvatar';

const EmployerJobs = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyName = searchParams.get('company') || '';
  const employerId = searchParams.get('employer_id') || '';
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employerInfo, setEmployerInfo] = useState(null);

  useEffect(() => {
    if (companyName || employerId) {
      fetchEmployerJobs();
    }
  }, [companyName, employerId]);

  const fetchEmployerJobs = async () => {
    setLoading(true);
    try {
      // Build query params - prefer employer_id for accuracy
      let queryParams = 'limit=100';
      if (employerId) {
        queryParams += `&employer_id=${encodeURIComponent(employerId)}`;
      } else if (companyName) {
        queryParams += `&company_name=${encodeURIComponent(companyName)}`;
      }
      
      const response = await api.get(`/jobs?${queryParams}`);
      const allJobs = response.data.jobs || [];
      setJobs(allJobs);
      
      // Get employer info from first job
      if (allJobs.length > 0) {
        const firstJob = allJobs[0];
        setEmployerInfo({
          company_name: firstJob.company_name,
          logo: firstJob.company_logo,
          is_prime: firstJob.is_prime_employer_job || false,
          total_jobs: allJobs.length
        });
      } else {
        // No jobs found but show company name
        setEmployerInfo({
          company_name: companyName,
          logo: null,
          is_prime: employerId?.startsWith('prime-') || false,
          total_jobs: 0
        });
      }
    } catch (error) {
      console.error('Error fetching employer jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return getAssetUrl(path);
  };

  // Dynamic SEO
  const pageTitle = employerInfo 
    ? `${employerInfo.company_name} Jobs - ${jobs.length} Open Positions | VVVSI`
    : `${companyName} Jobs | VVVSI`;
  
  const pageDescription = employerInfo
    ? `View all ${jobs.length} job openings at ${employerInfo.company_name}. Apply now for latest career opportunities in various departments.`
    : `View all job openings at ${companyName}. Find your dream job and apply today.`;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-slate-200 rounded-xl"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="h-64 bg-slate-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${companyName} jobs, ${companyName} careers, ${companyName} openings, job vacancies`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/employer-jobs?company=${encodeURIComponent(companyName)}`} />
      </Helmet>
      
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-slate-600 hover:text-teal-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to All Jobs
        </Button>

        {/* Employer Header */}
        {employerInfo && (
          <Card className="p-6 sm:p-8 mb-8 bg-white border-slate-200">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <CompanyAvatar 
                name={employerInfo.company_name}
                logoUrl={getImageUrl(employerInfo.logo)}
                size="xl"
              />
              <div className="flex-1 text-center sm:text-left">
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>
                  {employerInfo.company_name}
                </h1>
                <p className="text-slate-600 mb-4">
                  <Users className="w-4 h-4 inline mr-1" />
                  {jobs.length} Active Job Opening{jobs.length !== 1 ? 's' : ''}
                </p>
                <p className="text-slate-700">
                  Explore career opportunities at {employerInfo.company_name}. 
                  We are actively hiring for multiple positions across various departments.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Jobs Grid */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>
            All Open Positions ({jobs.length})
          </h2>
        </div>

        {jobs.length === 0 ? (
          <Card className="p-12 text-center bg-white border-slate-200">
            <Briefcase className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Active Jobs</h3>
            <p className="text-slate-600 mb-4">
              {companyName} doesn't have any active job openings at the moment.
            </p>
            <Button onClick={() => navigate('/')} className="bg-teal-600 hover:bg-teal-700">
              Browse All Jobs
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {jobs.map((job) => (
              <Card 
                key={job.id} 
                data-testid={`job-card-${job.id}`}
                className="group bg-white p-5 sm:p-6 rounded-xl border border-slate-200 hover:border-teal-500/50 hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => navigate(`/job/${job.id}`)}
              >
                {/* Header with Logo and Company */}
                <div className="flex items-start gap-4 mb-4">
                  <CompanyAvatar 
                    name={job.company_name}
                    logoUrl={getImageUrl(job.company_logo)}
                    size="lg"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-slate-900 group-hover:text-teal-600 transition-colors line-clamp-2 mb-1" style={{fontFamily: 'Outfit'}}>
                      {job.job_title}
                    </h3>
                    <p className="text-sm text-slate-600 truncate flex items-center gap-1">
                      <Building2 className="w-4 h-4 flex-shrink-0" />
                      {job.company_name}
                    </p>
                  </div>
                </div>
                
                {/* Job Type & Location */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                    {job.job_type}
                  </span>
                  <span className="text-sm text-slate-600 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {job.locations?.[0]?.city || 'Multiple Locations'}
                  </span>
                </div>
                
                {/* Job Details Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-slate-50 px-3 py-2 rounded-lg">
                    <p className="text-xs text-slate-500 mb-0.5">Salary</p>
                    <p className="text-sm font-medium text-slate-900">₹{job.min_salary} - {job.max_salary}</p>
                  </div>
                  <div className="bg-slate-50 px-3 py-2 rounded-lg">
                    <p className="text-xs text-slate-500 mb-0.5">Experience</p>
                    <p className="text-sm font-medium text-slate-900">{job.min_experience} - {job.max_experience} years</p>
                  </div>
                  {job.min_education && (
                    <div className="bg-purple-50 px-3 py-2 rounded-lg">
                      <p className="text-xs text-purple-600 mb-0.5">Education</p>
                      <p className="text-sm font-medium text-purple-900 truncate">{job.min_education}</p>
                    </div>
                  )}
                  <div className="bg-slate-50 px-3 py-2 rounded-lg">
                    <p className="text-xs text-slate-500 mb-0.5">Openings</p>
                    <p className="text-sm font-medium text-slate-900">{job.vacancies} positions</p>
                  </div>
                </div>
                
                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Posted {new Date(job.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                  <span className="text-sm font-medium text-teal-600 group-hover:text-teal-700">
                    View Details →
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default EmployerJobs;
